import type { Station } from '@reliance/types/models/Station';
import { createColumnHelper } from '@tanstack/react-table';
import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import BaseHtmlTable from '../table/BaseHtmlTable';

const columnHelper = createColumnHelper<Station>();

const TYPE_FILTER_OPTIONS = [
  { value: '', label: '(any)' },
  { value: 'Officer', label: 'Officer' },
  { value: 'Inmate', label: 'Inmate' },
  { value: 'Visitor', label: 'Visitor' },
  { value: 'Phone', label: 'Phone' },
  { value: 'Audio Visit', label: 'Audio Visit' },
];

const STATUS_FILTER_OPTIONS = [
  { value: '', label: '(any)' },
  { value: 'Active', label: 'Active' },
  { value: 'Inactive', label: 'Inactive' },
];

type TableProps = {
  rows: Station[];
  loading: boolean;
};

const Table: React.FC<TableProps> = ({ rows, loading }) => {
  const sortedRows = useMemo(
    () => [...rows].sort((a, b) => (b.id ?? 0) - (a.id ?? 0)),
    [rows]
  );

  const columnDefs = useMemo(
    () => [
      columnHelper.accessor('name', {
        id: 'name',
        header: 'Name',
        meta: { filterComponent: 'TextFilter' },
        filterFn: 'includesString',
      }),
      columnHelper.accessor((row) => row.type as string, {
        id: 'type',
        header: 'Type',
        meta: {
          filterComponent: 'SelectFilter',
          filterOptions: TYPE_FILTER_OPTIONS,
        },
        filterFn: (row, columnId, filterValue) => {
          if (filterValue == null || filterValue === '') {
            return true;
          }
          return row.getValue(columnId) === filterValue;
        },
      }),
      columnHelper.accessor('userId', {
        id: 'userId',
        header: 'User ID',
        meta: { filterComponent: 'TextFilter' },
        filterFn: 'includesString',
      }),
      columnHelper.accessor('status', {
        id: 'status',
        header: 'Status',
        meta: {
          filterComponent: 'SelectFilter',
          filterOptions: STATUS_FILTER_OPTIONS,
        },
        filterFn: (row, columnId, filterValue) => {
          if (filterValue == null || filterValue === '') {
            return true;
          }
          return row.getValue(columnId) === filterValue;
        },
      }),
      columnHelper.display({
        id: 'device',
        header: 'Device',
        enableColumnFilter: false,
        cell: (ctx) => {
          const station = ctx.row.original;
          if (station.type === 'Phone' || !station.mobileDevice) {
            return null;
          }
          return (
            <span>
              <Link to={`/mobile-device/view/${station.mobileDevice.id}`}>
                <i className="fas fa-mobile" /> {station.mobileDevice.name}
              </Link>
              <small> ({station.mobileDevice.status})</small>
            </span>
          );
        },
      }),
      columnHelper.display({
        id: 'actions',
        header: '',
        enableColumnFilter: false,
        cell: (ctx) => {
          const station = ctx.row.original;
          if (station.id == null) {
            return null;
          }
          return (
            <Link className="btn btn-secondary btn-sm" to={`/station/view/${station.id}`}>
              View
            </Link>
          );
        },
      }),
    ],
    []
  );

  return (
    <BaseHtmlTable
      rows={sortedRows}
      columnDefs={columnDefs}
      loading={loading}
      paginationMode="client"
      total={sortedRows.length}
      perPage={1000}
      pageSizeOptions={[100, 500, 1000]}
    />
  );
};

export default Table;
